/**
 * Form state + validação + build do request — Create Guardianship (POST).
 *
 * Espelha o CreateGuardianshipDto real: wardId/guardianId/type obrigatórios;
 * scope (objeto estruturado), basis (enum), effectiveFrom/Until (ISO),
 * documentReferenceId (string) opcionais. Datas do <input type=date> (YYYY-MM-DD)
 * convertem para ISO via toISODate (mesmo padrão do B4 report-form).
 */

import type {
  CreateGuardianshipRequest,
  GuardianshipBasis,
  GuardianshipScope,
  GuardianshipType,
} from './types';

export interface GuardianshipFormState {
  wardId: string;
  guardianId: string;
  type: GuardianshipType;
  scope: GuardianshipScope;
  basis: GuardianshipBasis | '';
  effectiveFrom: string;
  effectiveUntil: string;
  documentReferenceId: string;
}

export function initialGuardianshipForm(): GuardianshipFormState {
  return {
    wardId: '',
    guardianId: '',
    type: 'legal-guardian',
    scope: {},
    basis: '',
    effectiveFrom: '',
    effectiveUntil: '',
    documentReferenceId: '',
  };
}

/** Converte `YYYY-MM-DD` (UTC midnight) em ISO string; vazio → undefined. */
function toISODate(value: string): string | undefined {
  if (value.trim() === '') return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export function validateGuardianshipForm(form: GuardianshipFormState): string | null {
  if (form.wardId.trim() === '') return 'Informe o ID do representado (ward).';
  if (form.guardianId.trim() === '') return 'Informe o ID do representante (guardian).';
  if (form.wardId.trim() === form.guardianId.trim()) {
    return 'Ward e guardian devem ser pessoas distintas.';
  }
  const from = toISODate(form.effectiveFrom);
  const until = toISODate(form.effectiveUntil);
  if (from !== undefined && until !== undefined && new Date(until) <= new Date(from)) {
    return 'A data de término deve ser posterior à de início.';
  }
  return null;
}

/** Só inclui scope com ao menos um flag ativo (BE aceita objeto vazio, mas evitamos ruído). */
function compactScope(scope: GuardianshipScope): GuardianshipScope | undefined {
  const entries = Object.entries(scope).filter(([, v]) => v === true);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

export function buildCreateRequest(form: GuardianshipFormState): CreateGuardianshipRequest {
  const req: CreateGuardianshipRequest = {
    wardId: form.wardId.trim(),
    guardianId: form.guardianId.trim(),
    type: form.type,
  };
  const scope = compactScope(form.scope);
  if (scope !== undefined) req.scope = scope;
  if (form.basis !== '') req.basis = form.basis;
  const from = toISODate(form.effectiveFrom);
  if (from !== undefined) req.effectiveFrom = from;
  const until = toISODate(form.effectiveUntil);
  if (until !== undefined) req.effectiveUntil = until;
  if (form.documentReferenceId.trim() !== '') {
    req.documentReferenceId = form.documentReferenceId.trim();
  }
  return req;
}
